import { makeStyles } from "tss-react/mui";
import { MiMenu, MiStackedTypography } from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import {
  mdiPrinterOutline,
  mdiTrashCanOutline,
  mdiTrayArrowDown,
} from "@mdi/js";
import { Box, Typography } from "@mui/material";

export const DocumentCard = ({
  item: document = {},
  disablePrint = false,
  disableDelete = false,
  selectedDocumentId = "",
  attributes = (doc) => [doc?.category, doc?.orderTemplateName, doc?.tradeName],
  onDelete = () => {},
  onPrint = () => {},
  onSelect = () => {},
  onDownload = () => {},
}) => {
  const selected = document?.id === selectedDocumentId;
  const { classes, theme } = useStyles({ selected });
  return (
    <Box
      className={classes.cardContainer}
      onClick={() => onSelect(document)}
      data-testid="document-card"
    >
      <Box className={classes.innerLeftContainer}>
        <Typography
          color="primary.main"
          fontSize={theme.fontSize.medium}
          fontWeight={theme.fontWeight.semiBold}
          noWrap
        >
          {document?.name ||
            document?.metadata?.title ||
            document?.filename ||
            "Document Name"}
        </Typography>
        <Typography fontSize={theme.fontSize.small} color="grey">
          {attributes(document).filter(Boolean).join(" | ")}
        </Typography>
      </Box>

      <Box className={classes.innerRightContainer}>
        <MiStackedTypography
          title="Upload Date"
          description={
            formatDate(
              document?.createdOn,
              DATE_TIME_FORMATS.FORMAT_MM_DD_YY
            ) || "--"
          }
          descriptionProps={{
            fontWeight: theme.fontWeight.semiBold,
            width: 55,
            sx: { fontSize: `${theme.fontSize.small} !important` },
          }}
          sx={{ paddingVertical: 2 }}
        />
        <MiStackedTypography
          title="Uploaded By"
          description={document?.createdByName || "--"}
          descriptionProps={{
            fontWeight: theme.fontWeight.semiBold,
            width: 105,
            sx: { fontSize: `${theme.fontSize.small} !important` },
          }}
          sx={{ paddingVertical: 2 }}
        />
        <MiMenu
          data={[
            {
              label: "Download",
              action: (e) => {
                e.stopPropagation();
                onDownload(document);
              },
              icon: mdiTrayArrowDown,
              testId: "download-button",
            },
            {
              label: "Print",
              action: (e) => {
                e.stopPropagation();
                onPrint(document);
              },
              icon: mdiPrinterOutline,
              disabled: disablePrint,
              testId: "print-button",
            },
            {
              label: "Delete",
              action: (e) => {
                e.stopPropagation();
                onDelete(document);
              },
              icon: mdiTrashCanOutline,
              disabled: disableDelete,
              color: theme.palette.red.main,
              testId: "delete-button",
            },
          ]}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme, { selected }) => ({
  cardContainer: {
    display: "flex",
    flexDirection: "row",
    padding: theme.spacing(1, 1, 1, 2),
    alignItems: "center",
    borderRadius: 4,
    gap: theme.spacing(2),
    "&:hover": {
      cursor: "pointer",
      border: `1px solid ${theme.palette.primary.main}`,
    },
    transition: "border 0.1s ease",
    border: !selected
      ? `1px solid ${theme.palette.grey.lightAccent}`
      : `1px solid ${theme.palette.primary.main}`,
    backgroundColor: !selected
      ? theme.palette.white.main
      : theme.palette.primary.light,
    [theme.breakpoints.down("lg")]: {
      gap: theme.spacing(1),
      flexDirection: "column",
      alignItems: "stretch",
      padding: theme.spacing(2),
    },
  },
  innerLeftContainer: {
    width: "100%",
    [theme.breakpoints.up("lg")]: {
      width: "300px",
    },
  },
  innerRightContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing(4),
    justifyContent: "flex-start",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      gap: theme.spacing(1),
    },
    [theme.breakpoints.up("xl")]: {
      justifyContent: "flex-end",
    },
  },
  divider: {
    margin: theme.spacing(2, 2),
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
}));
